import { formatCompactNumber } from '../../lib/utils';
import { formaterResultat } from './TableauRapport';
import { formatPourcentSigne, margePourcent, nombre, pluriel, variationPourcent } from './reportHelpers';

/**
 * Ce que chaque rapport met dans le coin de l'en-tête de la page: ses chiffres
 * essentiels (miniatures), les autres indicateurs (volet) et ses alertes
 * (pastilles). Les rapports ne gardent dans leur corps que les tableaux.
 */

const tonResultat = (valeur) =>
  Number(valeur) < 0 ? 'critique' : Number(valeur) > 0 ? 'positif' : 'neutre';

const aideVariation = (courant, precedent, libellePrecedent) => {
  if (!precedent) return '';
  const v = variationPourcent(courant, precedent);
  return v === null ? '' : `${formatPourcentSigne(v, { signe: true })} par rapport à ${libellePrecedent}`;
};

// Un montant donné en texte (« 97 400 000 ») n'est pas abrégé par l'en-tête:
// la case d'un chiffre est étroite, et les grands montants débordaient sur le
// voisin. À partir du million, on fournit la forme courte (« 97,4 M »), le
// montant complet restant dans l'infobulle.
const court = (montant, { signe = false } = {}) => {
  const n = Number(montant);
  if (!Number.isFinite(n) || Math.abs(n) < 1_000_000) return undefined;
  return `${signe && n > 0 ? '+' : ''}${formatCompactNumber(n)}`;
};

const joindre = (...morceaux) => morceaux.filter(Boolean).join(' — ') || undefined;

/** Recettes, charges, résultat et marge, avec l'évolution au survol. */
const quatre = (cles, unite, aideRecettes, precedent, libellePrecedent) => {
  const { recettes, charges, resultat } = cles;
  const marge = margePourcent(recettes, resultat);
  return [
    {
      label: 'Recettes',
      valeur: nombre(recettes ?? 0) || '0',
      valeurCompacte: court(recettes),
      unite,
      aide: joindre(aideRecettes, aideVariation(recettes, precedent?.recettes, libellePrecedent)),
    },
    {
      label: 'Charges',
      valeur: nombre(charges ?? 0) || '0',
      valeurCompacte: court(charges),
      unite,
      aide: joindre(aideVariation(charges, precedent?.charges, libellePrecedent)),
    },
    {
      label: 'Résultat',
      valeur: formaterResultat(resultat ?? 0) || '0',
      valeurCompacte: court(resultat, { signe: true }),
      unite,
      ton: tonResultat(resultat),
      aide: joindre(
        'Recettes moins charges',
        precedent ? `${formaterResultat(precedent.resultat ?? 0) || '0'} ${unite} en ${libellePrecedent}` : '',
      ),
    },
    {
      label: 'Marge',
      valeur: marge === null ? '—' : formatPourcentSigne(marge),
      ton: marge === null ? 'neutre' : marge < 0 ? 'critique' : 'neutre',
      aide: marge === null ? 'Pas de recette ce mois' : 'Résultat rapporté aux recettes',
    },
  ];
};

/** Recettes, charges et résultat d'un rapport (compte d'exploitation ou synthèse). */
export function chiffresCles(onglet, report) {
  if (!report) return null;
  if (onglet === 'compte') {
    const t = report.totals || {};
    return { recettes: t.total_revenue ?? 0, charges: t.total_cost ?? 0, resultat: t.result ?? 0 };
  }
  if (onglet === 'synthese') {
    const t = report.total || {};
    return { recettes: t.revenue ?? 0, charges: t.charges ?? 0, resultat: t.result ?? 0 };
  }
  return null;
}

export function statsRapport(onglet, report, precedent = null, libellePrecedent = 'le mois précédent') {
  if (!report) return { stats: undefined, plus: null };
  const unite = report.currency_label || 'FCFA';

  if (onglet === 'compte') {
    const t = report.totals || {};
    return {
      stats: quatre(
        chiffresCles(onglet, report),
        unite,
        `${t.rotations ?? 0} ${pluriel(t.rotations, 'rotation', 'rotations')}`,
        precedent,
        libellePrecedent,
      ),
      plus: null,
    };
  }

  if (onglet === 'synthese') {
    const t = report.total || {};
    const nb = t.trucks_count ?? 0;
    return {
      stats: quatre(
        chiffresCles(onglet, report),
        unite,
        `${nb} ${pluriel(nb, 'camion', 'camions')}`,
        precedent,
        libellePrecedent,
      ),
      plus: null,
    };
  }

  if (onglet === 'loues') {
    const t = report.totals || {};
    const nb = t.trucks_count ?? (report.trucks || []).length;
    const ok = t.earned_count ?? (report.trucks || []).filter((c) => c.earned).length;
    return {
      stats: [
        { label: nb > 1 ? 'Camions loués' : 'Camion loué', valeur: nb, aide: `${t.rotations ?? 0} ${pluriel(t.rotations, 'rotation', 'rotations')} dans le mois` },
        {
          label: 'Ont rapporté',
          valeur: `${ok} sur ${nb}`,
          ton: ok === nb ? 'positif' : ok === 0 ? 'critique' : 'attention',
        },
        { label: 'Recettes', valeur: nombre(t.revenue ?? 0) || '0', valeurCompacte: court(t.revenue), unite },
        {
          label: 'Marge',
          valeur: formaterResultat(t.margin ?? 0) || '0',
          valeurCompacte: court(t.margin, { signe: true }),
          unite,
          ton: tonResultat(t.margin ?? 0),
          aide: 'Marge de la société : recettes moins charges et fret',
        },
      ],
      plus: null,
    };
  }

  if (onglet === 'bilan') {
    // Les chiffres du bilan (résultat, argent disponible, ce qu'on vous doit,
    // ce que vous devez) sont l'essentiel en haut du bilan lui-même: les
    // répéter en pastilles dans l'en-tête doublait la lecture. L'en-tête ne
    // garde que la flotte du mois, dans « Plus d'indicateurs ».
    if (!report.result_lines) return { stats: undefined, plus: null };
    const { fleet } = report;
    const lignes = [];
    if (fleet) {
      lignes.push(
        ['Camions de la flotte', `${fleet.trucks_count ?? 0} (${fleet.own_trucks ?? 0} de la société, ${fleet.rented_trucks ?? 0} ${pluriel(fleet.rented_trucks, 'loué', 'loués')})`],
        [`Rotations de ${report.columns?.month || 'ce mois'}`, String(fleet.rotations_month ?? 0)],
        ["Rotations de l'exercice", String(fleet.rotations_cumulative ?? 0)],
        ...(fleet.rented_out_trucks > 0 ? [['Camions donnés en location', String(fleet.rented_out_trucks)]] : []),
        ...(fleet.active_trucks !== undefined && fleet.active_trucks !== null ? [['Camions actifs ce mois', String(fleet.active_trucks)]] : []),
        ['Camions immobilisés', String(fleet.fixed_assets?.count ?? 0)],
      );
      if (fleet.fixed_assets?.count) {
        lignes.push(['Amortissement du mois', `${nombre(fleet.fixed_assets.depreciation_month ?? 0)} ${unite}`]);
      }
    }
    return { stats: undefined, plus: lignes.length > 0 ? lignes : null };
  }

  return { stats: undefined, plus: null };
}

/** Alertes d'un rapport: papiers échus, montants prévus. */
export function alertesRapport(onglet, report) {
  if (!report) return [];
  const liste = [];
  if (onglet === 'bilan') {
    const echus = report.documents_due?.already_expired_count ?? 0;
    if (echus > 0) {
      liste.push({
        niveau: 'critique',
        nombre: echus,
        court: pluriel(echus, 'papier échu', 'papiers échus'),
        texte: `${echus} ${pluriel(echus, 'papier de camion est déjà échu', 'papiers de camions sont déjà échus')}. À renouveler sans attendre.`,
      });
    }
    if (report.estimated) {
      liste.push({
        niveau: 'info',
        court: 'Montants prévus',
        texte: 'Montants prévus : ce bilan compte du fret pas encore facturé ou des charges pas encore payées. Ils sont marqués « Prévu ».',
      });
    }
  }
  return liste;
}

/** Saisies faites après la clôture du mois (état de clôture). */
export function alerteSaisiesTardives(statut) {
  const saisies = statut?.late_entries;
  const n = saisies?.total ?? 0;
  if (!statut?.closed || n <= 0) return null;
  const detail = saisies.by_kind?.length
    ? ` ${saisies.by_kind.map((genre) => `${genre.label} : ${genre.count}`).join(' · ')}.`
    : '';
  return {
    niveau: 'attention',
    nombre: n,
    court: pluriel(n, 'saisie après clôture', 'saisies après clôture'),
    texte: `${n} ${pluriel(n, 'saisie faite', 'saisies faites')} après la clôture.${detail} Elles ne sont pas dans le bilan figé à la clôture. Rien n'est bloqué : les chiffres affichés les comptent.`,
  };
}
